import {ReactElement, useMemo} from 'react';
import {Image, Link, useUrl} from '@shopify/hydrogen';
import {
  designYourOwnEvent1JPEG,
  designYourOwnEvent1WEBP,
} from '../../assets/img';
import {
  NewsArticleCategory,
  NewsPagesCommonSidebarPromotionTileType,
  NewsArticleListItemType,
} from '../../types';
import {formatPublishedAtDate} from '../../utils';
import classNames from 'classnames';
import WidgetArea from '../WidgetArea.client';

interface NewsSharedSidebarProps {
  sidebarPromotion: NewsPagesCommonSidebarPromotionTileType;
  publishedYears: ReadonlyArray<number>;
  newsArticles: ReadonlyArray<NewsArticleListItemType>;
}

export default function NewsSharedSidebar({
  sidebarPromotion,
  publishedYears,
  newsArticles,
}: NewsSharedSidebarProps): ReactElement {
  const {searchParams}: URL = useUrl();
  const categoryParam = searchParams.get('category');
  const yearParam = searchParams.get('year');

  const iterablePublishedYears = useMemo(() => {
    const currentYear = new Date().getFullYear();
    return publishedYears
      .filter((year) => year !== currentYear)
      .sort((a, b) => b - a);
  }, [publishedYears]);

  return (
    <WidgetArea title="Whisky News, Reviews &amp; More...">
      <div className="widgets-mobile-sticky" id="mobileWidgetsSticky">
        {sidebarPromotion && (
          <div className="widget-tile red-tile">
            <div className="widget-tile__img">
              <picture>
                <source srcSet={designYourOwnEvent1WEBP} type="image/webp" />
                <source srcSet={designYourOwnEvent1JPEG} type="image/jpeg" />
                <Image
                  src={designYourOwnEvent1JPEG}
                  width={800}
                  height={800}
                  alt={sidebarPromotion.title}
                />
              </picture>
            </div>
            <div className="widget-tile__content">
              <h2 className="widget-tile__title">
                <span
                  dangerouslySetInnerHTML={{__html: sidebarPromotion.title}}
                />
              </h2>
              <div className="widget-tile__text-alt">
                {sidebarPromotion.content_text}
              </div>
              <div className="widget-tile__button">
                <a
                  href={sidebarPromotion.button_url}
                  className="button button_color_white button_has_icon"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {sidebarPromotion.button_text}&nbsp;
                  <i className="icon-right-arrow1"></i>
                </a>
              </div>
            </div>
          </div>
        )}
        <div className="widget-section">
          <h4 className="title-2">Browse by topic</h4>
          <ul className="widget-icon-list">
            <li className="widget-icon-list__item">
              <Link to="/news?category=" className="widget-icon-list__link">
                <div className="widget-icon-list__icon">
                  <i className="icon-all-articles"></i>
                </div>
                <span className="widget-icon-list__link-text">
                  All Articles
                </span>
              </Link>
            </li>
            {Object.values(NewsArticleCategory).map(
              ({value, label, iconName}) => (
                <li key={value} className="widget-icon-list__item">
                  <Link
                    to={`/news?category=${value}${
                      yearParam ? `&year=${yearParam}` : ''
                    }`}
                    className={classNames('widget-icon-list__link', {
                      active: value === categoryParam,
                    })}
                  >
                    <div className="widget-icon-list__icon">
                      <i className={iconName}></i>
                    </div>
                    <span className="widget-icon-list__link-text">{label}</span>
                  </Link>
                </li>
              ),
            )}
          </ul>
        </div>
        {iterablePublishedYears.length > 0 && (
          <div className="widget-section">
            <h4 className="title-2">Whisky news archive</h4>
            {iterablePublishedYears.map((year) => (
              <Link
                key={year}
                to={`/news?year=${year}${
                  categoryParam ? `&category=${categoryParam}` : ''
                }`}
                className={classNames(
                  'button button_color_gray button_full button_type_link',
                  {
                    active: year === Number(yearParam),
                  },
                )}
              >
                {year} past whisky news
              </Link>
            ))}
          </div>
        )}
        {newsArticles.length > 0 && (
          <div className="widget-section">
            <h4 className="title-2">TWL Reviews</h4>
            <ul className="upcoming-events">
              {newsArticles.map(({uuid, slug, title, label, published_at}) => (
                <li key={uuid} className="upcoming-events__item">
                  <div className="upcoming-events__label tag-element">
                    {label}
                  </div>
                  <div className="upcoming-events__date date-element">
                    {formatPublishedAtDate(published_at)}
                  </div>
                  <h4 className="upcoming-events__title title-item">
                    <Link
                      to={`/news/${slug}`}
                      className="upcoming-events__link"
                    >
                      {title}
                    </Link>
                  </h4>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </WidgetArea>
  );
}
